import {
  Entity,
  generateFractionalIndexingKeyBetween,
  LiveData,
} from '@toeverything/infra';
import { map, of, switchMap } from 'rxjs';

import type { FolderStore } from '../stores/folder';
import { AuthService, type WorkspaceServerService } from '../../cloud';
import { FolderRole, type FolderPermission, type FolderPermissionDetails } from '../../db/schema/schema';
import type { WorkspacePermissionService, WorkspaceTeamService } from '../../permissions';

export class FolderNode extends Entity<{
  id: string | null;
}> {
  id = this.props.id;

  info$ = LiveData.from<{
    data: string;
    type: (string & {}) | 'folder' | 'doc' | 'tag' | 'collection';
    index: string;
    id: string;
    parentId?: string | null;
    creatorId?: string | null;
    permissions?: FolderPermission | null;
  } | null>(this.store.watchNodeInfo(this.id ?? ''), null);
  type$ = this.info$.map(info =>
    this.id === null ? 'folder' : (info?.type ?? '')
  );
  data$ = this.info$.map(info => info?.data);
  name$ = this.info$.map(info => (info?.type === 'folder' ? info.data : ''));


  children$ = LiveData.from<FolderNode[]>(
    // watch children if this is a folder, otherwise return empty array
    this.type$.pipe(
      switchMap(type =>
        type === 'folder'
          ? this.store
            .watchNodeChildren(this.id)
            .pipe(
              map(children =>
                children
                  .filter(e => this.filterInvalidChildren(e))
                  .map(child =>
                    this.framework.createEntity(FolderNode, child)
                  )
              )
            )
            .pipe()
          : of([])
      )
    ),
    []
  );
  sortedChildren$ = LiveData.computed(get => {
    return get(this.children$)
      .map(node => [node, get(node.index$)] as const)
      .sort((a, b) => (a[1] > b[1] ? 1 : -1))
      .map(([node]) => node);
  });
  index$ = this.info$.map(info => info?.index ?? '');

  permissions$ = this.info$.map(info => info?.permissions);
  defaultRole$ = this.permissions$.map(
    permissions => {
      return permissions?.defaultRole ?? FolderRole.None;
    }
  );

  private readonly authService: AuthService | undefined;

  isOwner$ = LiveData.computed(get => {
    const authService = this.authService;
    if (!authService) {
      return false;
    }
    const account = authService.session.account$.value;
    if (!account) {
      return false;
    }
    return get(this.info$)?.creatorId === account.id;
  });

  isManager$ = LiveData.computed(get => {
    const permissions = get(this.permissions$);
    if (!permissions || !this.authService) {
      return false;
    }
    const _isManager = permissions.permissions.some(permission => {
      return permission.role === FolderRole.Admin && permission.id === this.authService?.session.account$.value?.id;
    });

    return get(this.defaultRole$) === FolderRole.Admin || _isManager;
  });

  isEditor$ = LiveData.computed(get => {
    const permissions = get(this.permissions$);
    if (!permissions || !this.authService) {
      return false;
    }
    const _isEditor = permissions.permissions.some(permission => {
      return permission.role === FolderRole.Write && permission.id === this.authService?.session.account$.value?.id;
    });
    return get(this.defaultRole$) === FolderRole.Write || _isEditor;
  });

  constructor(readonly store: FolderStore, readonly workspaceServerService: WorkspaceServerService, readonly workspaceTeamService: WorkspaceTeamService, readonly workspacePermissionService: WorkspacePermissionService) {
    super();
    this.authService = this.workspaceServerService.server?.scope.get(AuthService);
  }

  contains(childId: string | null): boolean {
    if (!this.id) {
      return true;
    }
    if (!childId) {
      return false;
    }
    return this.store.isAncestor(childId, this.id);
  }

  beChildOf(parentId: string | null): boolean {
    if (!this.id) {
      return false;
    }
    if (!parentId) {
      return true;
    }
    return this.store.isAncestor(this.id, parentId);
  }

  filterInvalidChildren(child: any): boolean {
    if (this.id === null && child.type !== 'folder') {
      return false; // root node can only have folders
    }

    let creatorId: string | undefined;
    if (this.authService) {
      creatorId = this.authService.session.account$.value?.id;
    }

    if (child.type === 'folder') {

      const isOwner = this.workspacePermissionService.permission.isOwner$.value;
      if (isOwner) {
        return true; // admin can see all docs
      }

      if (!child.permissions) {
        return false;
      }
      if (child?.permissions?.defaultRole !== FolderRole.None) {
        return true;
      }

      const ids = this.workspaceTeamService.myTeamIds.ids$.value;

      const existPermission = child.permissions?.permissions?.filter((permission: FolderPermissionDetails) => {
        return permission.id === creatorId || ids?.includes(permission.id);
      }) as FolderPermissionDetails[];

      // get max role from permissions
      const maxRole = existPermission.reduce((max, permission) => {
        if (permission.role === FolderRole.Admin) {
          return FolderRole.Admin;
        } else if (permission.role === FolderRole.Write && max !== FolderRole.Admin) {
          return FolderRole.Write;
        } else if (permission.role === FolderRole.Read && max !== FolderRole.Admin && max !== FolderRole.Write) {
          return FolderRole.Read;
        } else {
          return max;
        }
      }, FolderRole.None);

      if (maxRole !== FolderRole.None) {
        return true; // show the folder if the user has any role other than None
      }

      if (child.creatorId !== creatorId && child.type === 'folder') {
        return false; // only show the folders created by the current user
      }
    }

    if (child.type === 'doc') {

      const isAdmin = this.workspacePermissionService.permission.isAdmin$.value;
      const isOwner = this.workspacePermissionService.permission.isOwner$.value;
      const docsPermission = this.workspacePermissionService.docsPermission.permissions$.value;

      if (isAdmin || isOwner) {
        return true; // admin can see all docs
      }

      if (!docsPermission) {
        return false; // no permissions, cannot see the doc
      }
      const docRole = docsPermission?.find(
        permission => permission.id === child.data
      )?.role;




      if (!docRole) {
        return false;
      }
      return +docRole > 0;
    }


    return true;
  }

  createFolder(name: string, index: string) {
    if (this.type$.value !== 'folder') {
      throw new Error('Cannot create folder on non-folder node');
    }



    let creatorId: string | undefined;
    if (this.authService) {
      creatorId = this.authService.session.account$.value?.id;
    }

    console.info('create folder', this.id, name, index, creatorId);
    return this.store.createFolder(this.id, name, index, creatorId);
  }

  createLink(
    type: 'doc' | 'tag' | 'collection',
    targetId: string,
    index: string
  ) {
    if (this.id === null) {
      throw new Error('Cannot create link on root node');
    }
    if (this.type$.value !== 'folder') {
      throw new Error('Cannot create link on non-folder node');
    }
    this.store.createLink(this.id, type, targetId, index);
  }

  delete() {
    if (this.id === null) {
      throw new Error('Cannot delete root node');
    }
    if (this.type$.value === 'folder') {
      this.store.removeFolder(this.id);
    } else {
      this.store.removeLink(this.id);
    }
  }

  moveHere(childId: string, index: string) {
    this.store.moveNode(childId, this.id, index);
  }

  rename(name: string) {
    if (this.id === null) {
      throw new Error('Cannot rename root node');
    }
    this.store.renameNode(this.id, name);
  }

  addPermission(
    permissions: FolderPermissionDetails[]
  ) {
    if (this.id === null) {
      throw new Error('Cannot add permission to root node');
    }
    this.store.addPermission(this.id, permissions);
  }

  removePermission(
    permission: FolderPermissionDetails
  ) {
    if (this.id === null) {
      throw new Error('Cannot remove permission from root node');
    }
    this.store.removePermission(this.id, permission);
  }

  updateDefaultRole(
    defaultRole: FolderRole
  ) {
    if (this.id === null) {
      throw new Error('Cannot update default role of root node');
    }
    this.store.updateDefaultRole(this.id, defaultRole);
  }



  indexAt(at: 'before' | 'after', targetId?: string) {
    if (!targetId) {
      if (at === 'before') {
        const first = this.sortedChildren$.value.at(0);
        return generateFractionalIndexingKeyBetween(
          null,
          first?.index$.value || null
        );
      } else {
        const last = this.sortedChildren$.value.at(-1);
        return generateFractionalIndexingKeyBetween(
          last?.index$.value || null,
          null
        );
      }
    } else {
      const sortedChildren = this.sortedChildren$.value;
      const targetIndex = sortedChildren.findIndex(
        node => node.id === targetId
      );
      if (targetIndex === -1) {
        throw new Error('Target node not found');
      }
      const target = sortedChildren[targetIndex];
      const before: FolderNode | null = sortedChildren[targetIndex - 1] || null;
      const after: FolderNode | null = sortedChildren[targetIndex + 1] || null;
      if (at === 'before') {
        return generateFractionalIndexingKeyBetween(
          before?.index$.value || null,
          target.index$.value
        );
      } else {
        return generateFractionalIndexingKeyBetween(
          target.index$.value,
          after?.index$.value || null
        );
      }
    }
  }
}
